import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';

export type PanelDock = 'left' | 'right';

export interface UsePanelResizeOptions {
  /** i18n label exposed to assistive tech through the handle. */
  label: string;
  /** localStorage key the last width is persisted under. */
  storageKey: string;
  /** Width used on first run and restored by double-clicking the handle. */
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** Layout edge the panel is docked to; decides drag and arrow-key direction. */
  dock: PanelDock;
}

/** Props to spread onto the handle element rendered by PanelResizeHandle. */
export interface PanelHandleDomProps {
  role: 'separator';
  'aria-orientation': 'vertical';
  tabIndex: 0;
  'aria-label': string;
  'aria-valuemin': number;
  'aria-valuemax': number;
  'aria-valuenow': number;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  onDoubleClick: () => void;
}

export interface PanelResizeState {
  width: number;
  isResizing: boolean;
  /** Restores defaultWidth (wired to double-click on the handle). */
  reset: () => void;
  handleProps: PanelHandleDomProps;
}

const BASE_STEP = 16;
const LARGE_STEP_FACTOR = 3;

/**
 * Panel width controller for resizable layout panels: pointer drag with
 * capture, keyboard adjustments, min/max clamping and localStorage
 * persistence. Width is committed on every move; persistence writes only
 * happen through `commit` so a failed write never blocks resizing.
 */
export function usePanelResize(options: UsePanelResizeOptions): PanelResizeState {
  const { label, storageKey, defaultWidth, minWidth, maxWidth, dock } = options;

  const clamp = useCallback(
    (value: number) => Math.min(maxWidth, Math.max(minWidth, Math.round(value))),
    [minWidth, maxWidth]
  );

  const [width, setWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return defaultWidth;
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw === null ? NaN : Number(JSON.parse(raw));
      return Number.isFinite(parsed) ? clamp(parsed) : defaultWidth;
    } catch {
      return defaultWidth;
    }
  });

  const commit = useCallback(
    (value: number) => {
      const next = clamp(value);
      setWidth(next);
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Persistence is best-effort; resizing keeps working without it.
      }
    },
    [clamp, storageKey]
  );

  const reset = useCallback(() => commit(defaultWidth), [commit, defaultWidth]);

  const [isResizing, setIsResizing] = useState(false);
  const dragStart = useRef<{ pointerX: number; startWidth: number } | null>(null);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.focus();
      dragStart.current = { pointerX: event.clientX, startWidth: width };
      setIsResizing(true);
    },
    [width]
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = dragStart.current;
      if (!start) return;
      const delta = event.clientX - start.pointerX;
      commit(dock === 'left' ? start.startWidth + delta : start.startWidth - delta);
    },
    [commit, dock]
  );

  const endResize = useCallback(() => {
    dragStart.current = null;
    setIsResizing(false);
  }, []);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const step = BASE_STEP * (event.shiftKey ? LARGE_STEP_FACTOR : 1);
      let next: number | null = null;
      if (event.key === 'ArrowUp') next = width + step;
      else if (event.key === 'ArrowDown') next = width - step;
      else if (event.key === (dock === 'left' ? 'ArrowRight' : 'ArrowLeft')) next = width + step;
      else if (event.key === (dock === 'left' ? 'ArrowLeft' : 'ArrowRight')) next = width - step;
      else if (event.key === 'Home') next = minWidth;
      else if (event.key === 'End') next = maxWidth;
      if (next === null) return;
      event.preventDefault();
      commit(next);
    },
    [commit, dock, maxWidth, minWidth, width]
  );

  // Lock the page cursor and selection while a drag is in flight so fast
  // pointer moves keep resizing instead of selecting text.
  useEffect(() => {
    if (!isResizing) return;
    const { style } = document.body;
    const previousCursor = style.cursor;
    const previousUserSelect = style.userSelect;
    style.cursor = 'col-resize';
    style.userSelect = 'none';
    return () => {
      style.cursor = previousCursor;
      style.userSelect = previousUserSelect;
    };
  }, [isResizing]);

  const handleProps: PanelHandleDomProps = {
    role: 'separator',
    'aria-orientation': 'vertical',
    tabIndex: 0,
    'aria-label': label,
    'aria-valuemin': minWidth,
    'aria-valuemax': maxWidth,
    'aria-valuenow': width,
    onPointerDown,
    onPointerMove,
    onPointerUp: endResize,
    onPointerCancel: endResize,
    onKeyDown,
    onDoubleClick: reset,
  };

  return { width, isResizing, reset, handleProps };
}
